import { useEffect, useRef, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ArrowLeft,
  Phone,
  Video,
  MoreVertical,
  RotateCcw,
  CheckCircle2,
  Clock,
  TrendingUp,
  FileText,
  Bell,
  Brain,
  Calendar,
  Mic,
  Play,
} from "lucide-react";
import { demoScript, dashboardInitial, planSummary } from "../data/demoScript";

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------
const fmt = (h, m) =>
  `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

// pre-compute timestamps that look natural across phases
const stampForStep = (i) => {
  // Loose mapping: phase 1 ~19:02, phase 2 ~19:10, etc.
  if (i < 14) return fmt(19, 2 + Math.min(i, 14));
  if (i < 23) return fmt(19, 16 + (i - 14));
  if (i < 28) return fmt(16, 45 + (i - 23) * 2);
  if (i < 33) return fmt(17, Math.min(i - 27, 8));
  if (i < 47) return fmt(17, 35 + Math.min(i - 33, 14));
  return fmt(20, 30 + Math.min(i - 47, 14));
};

// --------------------------------------------------------------------------
// Sub-components
// --------------------------------------------------------------------------
const ChatBubble = ({ msg, time }) => {
  const isAgent = msg.sender === "agent";
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 280, damping: 22 }}
      className={`max-w-[82%] px-3 py-2 rounded-2xl text-[13.5px] leading-snug shadow-sm break-words ${
        isAgent
          ? "self-start bg-white text-gray-800 rounded-tl-sm"
          : "self-end bg-[#DCF8C6] text-gray-800 rounded-tr-sm"
      }`}
      data-testid={`chat-bubble-${msg.sender}`}
    >
      {msg.role && !isAgent && (
        <div className="text-[10px] font-semibold text-[#1F5945] mb-0.5">
          {msg.role}
        </div>
      )}
      {msg.voice ? (
        <div className="flex items-center gap-2 py-1 pr-2 min-w-[140px]">
          <span className="w-7 h-7 rounded-full bg-[#25D366] text-[#0A2914] flex items-center justify-center">
            <Play className="w-3.5 h-3.5 fill-current" />
          </span>
          <div className="flex items-end gap-0.5 h-5 flex-1">
            {[3, 6, 9, 5, 8, 4, 7, 5, 9, 6, 4, 8, 5, 6, 9, 4].map((h, i) => (
              <span
                key={i}
                className="w-0.5 rounded-full bg-[#1F5945]"
                style={{ height: `${h * 1.6}px`, opacity: 0.7 }}
              />
            ))}
          </div>
          <Mic className="w-3.5 h-3.5 text-[#1F5945]" />
          <span className="text-[10px] text-gray-500 ml-0.5">
            {msg.duration || "0:12"}
          </span>
        </div>
      ) : (
        msg.text
      )}
      <div
        className={`text-[10px] mt-1 ${
          isAgent ? "text-gray-400 text-right" : "text-gray-500 text-right"
        }`}
      >
        {time} {isAgent ? "" : "✓✓"}
      </div>
    </motion.div>
  );
};

const ContextPill = ({ label, sublabel }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    className="self-center my-2 text-center"
  >
    <div className="inline-block bg-[#E1F2FB] text-[#54656F] rounded-md px-2.5 py-1 text-[10px] font-medium">
      {label}
    </div>
    {sublabel && (
      <div className="text-[10px] text-gray-400 mt-0.5">{sublabel}</div>
    )}
  </motion.div>
);

const PlanCardBubble = () => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ type: "spring", stiffness: 280, damping: 22 }}
    className="self-start max-w-[88%] bg-white rounded-2xl rounded-tl-sm p-3 shadow-sm border border-[#E0E0DA]"
    data-testid="chat-plan-card"
  >
    <div className="flex items-center gap-2 text-[10px] uppercase font-bold tracking-wider text-[#189346]">
      <Calendar className="w-3 h-3" />
      Plan d'étude proposé
    </div>
    <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]">
      <div className="text-gray-500">Durée</div>
      <div className="text-gray-800 font-medium text-right">
        {planSummary.duration}
      </div>
      <div className="text-gray-500">Jours</div>
      <div className="text-gray-800 font-medium text-right">
        {planSummary.days}
      </div>
      <div className="text-gray-500">Plage</div>
      <div className="text-gray-800 font-medium text-right">
        {planSummary.window}
      </div>
      <div className="text-gray-500">Période</div>
      <div className="text-gray-800 font-medium text-right">
        {planSummary.weeks}
      </div>
      <div className="text-gray-500">Total</div>
      <div className="text-[#189346] font-semibold text-right">
        {planSummary.total} séances
      </div>
    </div>
  </motion.div>
);

const ReportCardBubble = ({ dashboard }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ type: "spring", stiffness: 280, damping: 22 }}
    className="self-start max-w-[88%] bg-white rounded-2xl rounded-tl-sm p-3 shadow-sm border border-[#E0E0DA]"
    data-testid="chat-report-card"
  >
    <div className="flex items-center gap-2 text-[10px] uppercase font-bold tracking-wider text-[#189346]">
      <FileText className="w-3 h-3" />
      Bilan de la semaine
    </div>
    <div className="mt-1.5 text-[12px]">
      <div className="flex justify-between py-0.5">
        <span className="text-gray-500">Séances</span>
        <span className="text-gray-800 font-medium">
          {dashboard.weekProgress.completed}/{dashboard.weekProgress.planned}{" "}
          réalisées
        </span>
      </div>
      <div className="flex justify-between py-0.5">
        <span className="text-gray-500">Temps total</span>
        <span className="text-gray-800 font-medium">
          {dashboard.weekMinutes} min
        </span>
      </div>
      <div className="flex justify-between py-0.5">
        <span className="text-gray-500">Matières</span>
        <span className="text-gray-800 font-medium">Maths, Français</span>
      </div>
      <div className="flex justify-between py-0.5">
        <span className="text-gray-500">Tendance</span>
        <span className="text-[#189346] font-semibold">🟢 Régulier</span>
      </div>
    </div>
  </motion.div>
);

const Typing = () => (
  <motion.div
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 6 }}
    className="self-start bg-white rounded-2xl rounded-tl-sm px-3 py-2.5 shadow-sm flex items-center gap-1"
    data-testid="chat-typing-indicator"
  >
    <span className="typing-dot" />
    <span className="typing-dot" />
    <span className="typing-dot" />
  </motion.div>
);

const DashStat = ({ icon: Icon, label, value, accent }) => (
  <div
    className="rounded-2xl bg-white border border-gray-100 p-3 shadow-[0_1px_0_rgba(0,0,0,0.04)]"
    data-testid={`dash-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="flex items-center gap-2">
      <span
        className="w-7 h-7 rounded-lg flex items-center justify-center"
        style={{ background: accent || "#E9FBF0", color: "#189346" }}
      >
        <Icon className="w-3.5 h-3.5" />
      </span>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
        {label}
      </div>
    </div>
    <div className="mt-2 text-lg font-display font-bold text-[#122118]">
      {value}
    </div>
  </div>
);

const OrchestratorEvent = ({ label, when }) => (
  <motion.div
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    className="flex items-start gap-3"
  >
    <div className="relative">
      <div className="w-2 h-2 rounded-full bg-[#25D366] mt-1.5 pulse-dot" />
    </div>
    <div className="flex-1">
      <div className="text-sm font-medium text-[#122118]">{label}</div>
      <div className="text-xs text-gray-500">{when}</div>
    </div>
  </motion.div>
);

// --------------------------------------------------------------------------
// Main Modal Component
// --------------------------------------------------------------------------
const ChatDemo = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [messages, setMessages] = useState([]); // chat / context / plan_card / report_card
  const [typing, setTyping] = useState(false);
  const [events, setEvents] = useState([]);
  const [dashboard, setDashboard] = useState(dashboardInitial);
  const [running, setRunning] = useState(false);
  const scrollRef = useRef(null);
  const timersRef = useRef([]);

  useEffect(() => {
    if (open) {
      const orig = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = orig;
      };
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, typing]);

  const clearTimers = () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  };

  const reset = () => {
    clearTimers();
    setStep(0);
    setMessages([]);
    setTyping(false);
    setEvents([]);
    setDashboard(dashboardInitial);
    setRunning(false);
  };

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    const t = setTimeout(() => setRunning(true), 350);
    timersRef.current.push(t);
    return () => clearTimers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Drive the script
  useEffect(() => {
    if (!running) return;
    if (step >= demoScript.length) {
      setRunning(false);
      return;
    }
    const item = demoScript[step];
    const time = stampForStep(step);

    const t1 = setTimeout(() => {
      // ----- CHAT -----
      if (item.type === "chat") {
        setTyping(true);
        const t2 = setTimeout(() => {
          setTyping(false);
          setMessages((prev) => [
            ...prev,
            {
              kind: "chat",
              id: `${step}-${Date.now()}`,
              sender: item.sender,
              text: item.text,
              role: item.role,
              voice: item.voice,
              duration: item.duration,
              time,
            },
          ]);
          setStep((s) => s + 1);
        }, item.typing || 800);
        timersRef.current.push(t2);
        return;
      }

      // ----- CONTEXT (day separator) -----
      if (item.type === "context") {
        setMessages((prev) => [
          ...prev,
          {
            kind: "context",
            id: `${step}-${Date.now()}`,
            label: item.label,
            sublabel: item.sublabel,
          },
        ]);
        setStep((s) => s + 1);
        return;
      }

      // ----- PLAN CARD -----
      if (item.type === "plan_card") {
        setMessages((prev) => [
          ...prev,
          { kind: "plan_card", id: `${step}-${Date.now()}` },
        ]);
        setStep((s) => s + 1);
        return;
      }

      // ----- REPORT CARD -----
      if (item.type === "report_card") {
        setMessages((prev) => [
          ...prev,
          { kind: "report_card", id: `${step}-${Date.now()}` },
        ]);
        setStep((s) => s + 1);
        return;
      }

      // ----- DASHBOARD EVENT -----
      if (item.type === "dashboard") {
        setEvents((prev) => [
          ...prev,
          {
            id: `${step}-${Date.now()}`,
            label: item.label,
            when: time,
            event: item.event,
          },
        ]);
        setDashboard((prev) => {
          switch (item.event) {
            case "plan_drafted":
              return { ...prev, status: "Plan en attente d'acceptation" };
            case "parent_accepted":
              return { ...prev, parentAccepted: true };
            case "student_accepted":
              return { ...prev, studentAccepted: true };
            case "plan_active":
              return {
                ...prev,
                planActive: true,
                status: "Plan actif ✅",
                weekProgress: { planned: 4, completed: 0 },
              };
            case "session_started":
              return {
                ...prev,
                status: "Étude en cours…",
                lastSubject: "Mathématiques",
              };
            case "session_completed":
              return {
                ...prev,
                status: "Séance complétée ✅",
                weekProgress: {
                  ...prev.weekProgress,
                  completed: prev.weekProgress.completed + 1,
                },
                weekMinutes: prev.weekMinutes + 35,
                lastSubject: "Mathématiques",
              };
            case "report_ready":
              return { ...prev, status: "Rapport envoyé 📩" };
            default:
              return prev;
          }
        });
        setStep((s) => s + 1);
        return;
      }

      // fallback
      setStep((s) => s + 1);
    }, item.delay || 500);

    timersRef.current.push(t1);
    return () => clearTimeout(t1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, step]);

  const completionPct = useMemo(() => {
    const { planned, completed } = dashboard.weekProgress;
    if (planned === 0) return 0;
    return Math.min(100, Math.round((completed / planned) * 100));
  }, [dashboard]);

  const isFinished = step >= demoScript.length && !running;

  // current phase indicator
  const phase = useMemo(() => {
    if (!dashboard.planActive && !dashboard.parentAccepted)
      return { num: 1, label: "Création du plan" };
    if (!dashboard.planActive)
      return { num: 2, label: "Acceptation mutuelle" };
    if (dashboard.status === "Étude en cours…")
      return { num: 3, label: "Séance d'étude" };
    if (dashboard.status === "Séance complétée ✅")
      return { num: 4, label: "Bilan de séance" };
    if (dashboard.status === "Rapport envoyé 📩")
      return { num: 5, label: "Rapport parent" };
    return { num: 3, label: "Rappels & exécution" };
  }, [dashboard]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="demo-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
          data-testid="chat-demo-modal"
        >
          {/* backdrop */}
          <motion.div
            className="absolute inset-0 bg-[#0B1A12]/70 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 220, damping: 24 }}
            className="relative w-full max-w-6xl bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100"
          >
            {/* Top bar */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#25D366] pulse-dot" />
                <div>
                  <div className="text-sm font-semibold text-[#122118]">
                    Démo en direct — Orchestrateur Plan Étude
                  </div>
                  <div className="text-[11px] text-gray-500">
                    Phase {phase.num}/5 · {phase.label}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    reset();
                    setTimeout(() => setRunning(true), 250);
                  }}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-full hover:bg-gray-50 transition-colors"
                  data-testid="demo-restart-button"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Rejouer</span>
                </button>
                <button
                  onClick={onClose}
                  className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-900 transition-colors"
                  aria-label="Fermer"
                  data-testid="demo-close-button"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 max-h-[80vh]">
              {/* LEFT — Chat phone */}
              <div className="lg:col-span-5 bg-gradient-to-br from-[#F0F4F2] to-[#E9FBF0] p-6 flex items-center justify-center">
                <div className="relative w-full max-w-[340px] aspect-[9/19] mx-auto">
                  <div className="absolute inset-0 rounded-[44px] bg-[#0e1311] p-3 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.4)]">
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-6 bg-black rounded-full z-30" />
                    <div className="relative w-full h-full rounded-[34px] overflow-hidden bg-white flex flex-col">
                      <div className="bg-[#075E54] text-white px-4 pt-8 pb-3 flex items-center gap-3 z-20">
                        <ArrowLeft className="w-5 h-5 opacity-80" />
                        <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center text-base font-semibold">
                          PÉ
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm leading-tight truncate">
                            Plan Étude
                          </div>
                          <div className="text-[11px] text-white/70 flex items-center gap-1.5">
                            {typing ? "écrit…" : "en ligne"}
                          </div>
                        </div>
                        <Video className="w-5 h-5 opacity-80" />
                        <Phone className="w-4 h-4 opacity-80" />
                        <MoreVertical className="w-4 h-4 opacity-80" />
                      </div>

                      <div
                        ref={scrollRef}
                        className="chat-scroll flex-1 wa-bg px-3 py-3 flex flex-col gap-2 overflow-y-auto"
                      >
                        {messages.map((m) => {
                          if (m.kind === "context") {
                            return (
                              <ContextPill
                                key={m.id}
                                label={m.label}
                                sublabel={m.sublabel}
                              />
                            );
                          }
                          if (m.kind === "plan_card") {
                            return <PlanCardBubble key={m.id} />;
                          }
                          if (m.kind === "report_card") {
                            return (
                              <ReportCardBubble
                                key={m.id}
                                dashboard={dashboard}
                              />
                            );
                          }
                          return (
                            <ChatBubble key={m.id} msg={m} time={m.time} />
                          );
                        })}
                        <AnimatePresence>{typing && <Typing />}</AnimatePresence>

                        {isFinished && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="self-center text-[11px] mt-2 px-3 py-1.5 rounded-full bg-[#E9FBF0] text-[#189346] font-medium border border-[#25D366]/20"
                          >
                            Démo terminée — Cliquez sur « Rejouer »
                          </motion.div>
                        )}
                      </div>

                      <div className="bg-[#F0F2F5] px-3 py-2 flex items-center gap-2">
                        <div className="flex-1 bg-white rounded-full px-3 py-1.5 text-xs text-gray-400">
                          Message
                        </div>
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ background: "#25D366" }}
                        >
                          <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white">
                            <path d="M2 21l21-9L2 3v7l15 2-15 2z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT — Orchestrator + Parent dashboard */}
              <div className="lg:col-span-7 p-6 sm:p-7 overflow-y-auto chat-scroll">
                {/* Orchestrator banner */}
                <div className="rounded-2xl border border-[#25D366]/20 bg-[#F8FAF9] p-4 flex items-start gap-3.5">
                  <div
                    className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
                    style={{ background: "#0A2914" }}
                  >
                    <Brain className="w-5 h-5 text-[#25D366]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-semibold text-[#122118]">
                        StudyPlanner Orchestrator
                      </span>
                      <span className="text-[10px] uppercase font-bold tracking-wider text-[#189346] bg-[#E9FBF0] px-2 py-0.5 rounded-full">
                        actif
                      </span>
                    </div>
                    <p className="text-sm text-[#4a5f53] mt-1 leading-relaxed">
                      Décide quand envoyer les rappels, recueille les bilans,
                      met à jour la fiche élève et compile le rapport parent.
                    </p>
                  </div>
                </div>

                {/* Plan summary card */}
                <div
                  className={`mt-5 rounded-2xl p-4 border transition-all ${
                    dashboard.planActive
                      ? "bg-[#E9FBF0] border-[#25D366]/30"
                      : "bg-white border-gray-100"
                  }`}
                  data-testid="dash-plan-summary"
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#189346]" />
                      <span className="font-display font-semibold text-[#122118]">
                        Plan d'étude — {dashboard.studentName}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[11px]">
                      <span
                        className={`px-2 py-0.5 rounded-full font-semibold ${
                          dashboard.parentAccepted
                            ? "bg-[#E9FBF0] text-[#189346]"
                            : "bg-gray-100 text-gray-500"
                        }`}
                        data-testid="parent-status"
                      >
                        {dashboard.parentAccepted ? "✓" : "○"} Parent
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full font-semibold ${
                          dashboard.studentAccepted
                            ? "bg-[#E9FBF0] text-[#189346]"
                            : "bg-gray-100 text-gray-500"
                        }`}
                        data-testid="student-status"
                      >
                        {dashboard.studentAccepted ? "✓" : "○"} Élève
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2 text-[12px]">
                    <div>
                      <div className="text-gray-500">Durée</div>
                      <div className="font-semibold text-[#122118]">
                        {planSummary.duration}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Jours</div>
                      <div className="font-semibold text-[#122118]">
                        {planSummary.days}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Plage</div>
                      <div className="font-semibold text-[#122118]">
                        {planSummary.window}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Période</div>
                      <div className="font-semibold text-[#122118]">
                        {planSummary.weeks}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Total</div>
                      <div className="font-semibold text-[#189346]">
                        {planSummary.total} séances
                      </div>
                    </div>
                  </div>
                </div>

                {/* Parent dashboard */}
                <div className="mt-5">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="text-[11px] uppercase font-bold tracking-wider text-[#189346]">
                        Tableau de bord parent
                      </div>
                      <h3 className="font-display text-lg font-bold text-[#122118] mt-0.5">
                        Suivi de la semaine
                      </h3>
                    </div>
                    <div className="text-xs px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 font-medium">
                      {dashboard.status}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                    <DashStat
                      icon={CheckCircle2}
                      label="Séances"
                      value={`${dashboard.weekProgress.completed}/${dashboard.weekProgress.planned}`}
                    />
                    <DashStat
                      icon={Clock}
                      label="Minutes"
                      value={`${dashboard.weekMinutes} min`}
                    />
                    <DashStat
                      icon={TrendingUp}
                      label="Régularité"
                      value={`${completionPct}%`}
                    />
                    <DashStat
                      icon={FileText}
                      label="Matière"
                      value={dashboard.lastSubject}
                    />
                  </div>

                  <div className="mt-4 rounded-2xl border border-gray-100 bg-white p-4">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                      <span>Progression de la semaine</span>
                      <span className="font-semibold text-[#122118]">
                        {completionPct}%
                      </span>
                    </div>
                    <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: "#25D366" }}
                        initial={{ width: 0 }}
                        animate={{ width: `${completionPct}%` }}
                        transition={{ type: "spring", stiffness: 120, damping: 20 }}
                      />
                    </div>
                  </div>
                </div>

                {/* Orchestrator events */}
                <div className="mt-5">
                  <div className="text-[11px] uppercase font-bold tracking-wider text-gray-500 mb-2">
                    Événements de l'orchestrateur
                  </div>
                  <div className="rounded-2xl border border-gray-100 bg-white p-4 space-y-3 min-h-[120px] max-h-[180px] overflow-y-auto chat-scroll">
                    {events.length === 0 ? (
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <Bell className="w-4 h-4" />
                        En attente d'activité…
                      </div>
                    ) : (
                      events.map((e) => (
                        <OrchestratorEvent
                          key={e.id}
                          label={e.label}
                          when={e.when}
                        />
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ChatDemo;
